import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";

export interface WorkingSet {
  id: string;
  concept_id: string;
  name: string;
  purpose: string | null;
  paper_count: number;
  created_at: string;
  created_from_filter: Record<string, unknown> | null;
}

export function useWorkingSets(conceptId: string) {
  return useQuery({
    queryKey: ["working-sets", conceptId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/working-sets`)
        .json<WorkingSet[]>(),
    enabled: !!conceptId,
  });
}

export function useWorkingSetPaperIds(conceptId: string, wsId: string | undefined) {
  // Lightweight query to get paper IDs in a working set
  // Used for client-side filtering of the papers array
  return useQuery({
    queryKey: ["working-set-papers", wsId],
    queryFn: () =>
      apiClient
        .get(`api/concepts/${conceptId}/working-sets/${wsId}/paper-ids`)
        .json<{ paper_ids: string[] }>(),
    enabled: !!wsId && !!conceptId,
    // Paper IDs only change through our mutations (addPapers / removePapers
    // / seedFromFilter / saveAsWorkingSet), all of which explicitly
    // invalidate this key. With the default staleTime: 0, becoming newly
    // enabled (e.g. when saveAsWorkingSet swaps wsId from undefined →
    // realId) triggers a background refetch even when the cache is
    // freshly seeded — which races against the still-in-flight addPapers
    // POST and surfaces an empty paper_ids list. Marking it never-stale
    // makes the seeded cache authoritative until an explicit invalidate.
    staleTime: Infinity,
  });
}

export function useWorkingSetActions(conceptId: string) {
  const queryClient = useQueryClient();
  const key = ["working-sets", conceptId];

  const createWorkingSet = useMutation({
    mutationFn: (data: { name: string; purpose?: string; created_from_filter?: Record<string, unknown> }) =>
      apiClient.post(`api/concepts/${conceptId}/working-sets`, { json: data }).json<WorkingSet>(),
    // No optimistic update: creation requires a server-generated ID
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const updateWorkingSet = useMutation({
    mutationFn: (data: { wsId: string; name?: string; purpose?: string }) =>
      apiClient.patch(`api/concepts/${conceptId}/working-sets/${data.wsId}`, {
        json: { name: data.name, purpose: data.purpose },
      }).json<WorkingSet>(),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<WorkingSet[]>(key);
      queryClient.setQueryData<WorkingSet[]>(key, (old) =>
        old?.map((ws) =>
          ws.id === vars.wsId
            ? { ...ws, ...(vars.name !== undefined && { name: vars.name }), ...(vars.purpose !== undefined && { purpose: vars.purpose }) }
            : ws
        )
      );
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) queryClient.setQueryData(key, context.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const deleteWorkingSet = useMutation({
    mutationFn: (wsId: string) =>
      apiClient.delete(`api/concepts/${conceptId}/working-sets/${wsId}`),
    onMutate: async (wsId) => {
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<WorkingSet[]>(key);
      queryClient.setQueryData<WorkingSet[]>(key, (old) =>
        old?.filter((ws) => ws.id !== wsId)
      );
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) queryClient.setQueryData(key, context.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });

  const addPapers = useMutation({
    mutationFn: (data: { wsId: string; paperIds: string[] }) =>
      apiClient.post(`api/concepts/${conceptId}/working-sets/${data.wsId}/papers`, {
        json: { paper_ids: data.paperIds },
      }).json<{ added: number }>(),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<WorkingSet[]>(key);
      queryClient.setQueryData<WorkingSet[]>(key, (old) =>
        old?.map((ws) =>
          ws.id === vars.wsId
            ? { ...ws, paper_count: ws.paper_count + vars.paperIds.length }
            : ws
        )
      );
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) queryClient.setQueryData(key, context.prev);
    },
    onSettled: (_d, _e, vars) => {
      queryClient.invalidateQueries({ queryKey: key });
      queryClient.invalidateQueries({ queryKey: ["working-set-papers", vars.wsId] });
    },
  });

  const seedFromFilter = useMutation({
    mutationFn: (data: { wsId: string; paperIds: string[]; filter: Record<string, unknown> }) =>
      apiClient.post(`api/concepts/${conceptId}/working-sets/${data.wsId}/seed-from-filter`, {
        json: { paper_ids: data.paperIds, filter: data.filter },
      }).json<{ added: number }>(),
    onSettled: (_d, _e, vars) => {
      queryClient.invalidateQueries({ queryKey: key });
      queryClient.invalidateQueries({ queryKey: ["working-set-papers", vars.wsId] });
    },
  });

  const removePapers = useMutation({
    mutationFn: (data: { wsId: string; paperIds: string[] }) =>
      apiClient.delete(`api/concepts/${conceptId}/working-sets/${data.wsId}/papers`, {
        json: { paper_ids: data.paperIds },
      }).json<{ removed: number }>(),
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<WorkingSet[]>(key);
      queryClient.setQueryData<WorkingSet[]>(key, (old) =>
        old?.map((ws) =>
          ws.id === vars.wsId
            ? { ...ws, paper_count: Math.max(0, ws.paper_count - vars.paperIds.length) }
            : ws
        )
      );
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) queryClient.setQueryData(key, context.prev);
    },
    onSettled: (_d, _e, vars) => {
      queryClient.invalidateQueries({ queryKey: key });
      queryClient.invalidateQueries({ queryKey: ["working-set-papers", vars.wsId] });
    },
  });

  /**
   * Optimistically create a WS pre-seeded with paper IDs. Returns a synchronous
   * tempId so the caller can swap their active wsId immediately. The create +
   * addPapers POSTs run in the background; on success the tempId is reconciled
   * to the real id in both caches and `args.onSuccess` fires so the caller can
   * swap wsId from temp → real. On error the cache rolls back and
   * `args.onError` fires so the caller can restore prior UI state (e.g.
   * the camp scope banner).
   */
  const saveAsWorkingSet = useCallback(
    (args: {
      name: string;
      paperIds: string[];
      source?: string;
      onSuccess?: (realWs: WorkingSet, tempId: string) => void;
      onError?: (err: Error) => void;
    }): string => {
      const tempId = `temp-${crypto.randomUUID()}`;
      const optimisticWs: WorkingSet = {
        id: tempId,
        concept_id: conceptId,
        name: args.name,
        purpose: null,
        paper_count: args.paperIds.length,
        created_at: new Date().toISOString(),
        created_from_filter: args.source ? { source: args.source } : null,
      };
      const wsKey = ["working-sets", conceptId];
      const prev = queryClient.getQueryData<WorkingSet[]>(wsKey);
      queryClient.setQueryData<WorkingSet[]>(wsKey, (old) => [
        ...(old ?? []),
        optimisticWs,
      ]);
      queryClient.setQueryData(["working-set-papers", tempId], {
        paper_ids: args.paperIds,
      });

      void (async () => {
        try {
          const realWs = await apiClient
            .post(`api/concepts/${conceptId}/working-sets`, {
              json: {
                name: args.name,
                created_from_filter: args.source
                  ? { source: args.source }
                  : undefined,
              },
            })
            .json<WorkingSet>();
          // Swap temp → real in both caches, keeping the optimistic paper_count
          // so the count doesn't briefly drop to 0 before addPapers persists.
          queryClient.setQueryData<WorkingSet[]>(wsKey, (old) =>
            old?.map((w) =>
              w.id === tempId
                ? { ...realWs, paper_count: args.paperIds.length }
                : w,
            ),
          );
          queryClient.setQueryData(["working-set-papers", realWs.id], {
            paper_ids: args.paperIds,
          });
          // Fire onSuccess immediately so the caller swaps store.wsId from
          // tempId → realWs.id BEFORE the addPapers POST round-trip. If we
          // wait until after addPapers (or even just remove the tempId
          // cache here), there's a render window where store still says
          // tempId but the tempId paper-ids cache is gone — useFilteredPapers
          // falls back to trawledPapers (full corpus) for ~the addPapers
          // duration, producing a small→full→small flicker. Leaving the
          // tempId cache in place lets tanstack-query gc it naturally.
          args.onSuccess?.(realWs, tempId);
          // Use raw apiClient for addPapers — addPapers.mutate's onMutate
          // would double-count paper_count on top of our optimistic value.
          await apiClient
            .post(
              `api/concepts/${conceptId}/working-sets/${realWs.id}/papers`,
              { json: { paper_ids: args.paperIds } },
            )
            .json();
          queryClient.invalidateQueries({ queryKey: wsKey });
          queryClient.invalidateQueries({
            queryKey: ["working-set-papers", realWs.id],
          });
        } catch (e) {
          queryClient.setQueryData(wsKey, prev);
          queryClient.removeQueries({
            queryKey: ["working-set-papers", tempId],
          });
          args.onError?.(e as Error);
        }
      })();

      return tempId;
    },
    [queryClient, conceptId],
  );

  return { createWorkingSet, updateWorkingSet, deleteWorkingSet, addPapers, removePapers, seedFromFilter, saveAsWorkingSet };
}
